import React, { ReactElement } from 'react';
import { HiOutlineCheckCircle, HiOutlinePencil, HiOutlineTrash, HiOutlineXMark, HiPlus } from 'react-icons/hi2';
import { ImSpinner2 } from 'react-icons/im';
import toast from 'react-hot-toast';

export interface ManualRowValues {
  hour: number;
  minute: number;
  temperature: number;
  humidityPct: number;
  baroPa: number | null;
}

/** Shared row form for adding (`editingWeatherRow === -1`) or editing a manual weather entry. */
const validateAndSubmitWeatherRow = (
  e: React.FormEvent<HTMLFormElement>,
  onSave: (values: ManualRowValues) => void,
) => {
  e.preventDefault();
  const formData = new FormData(e.currentTarget);
  const hourStr = ((formData.get('hour') as string) || '').trim();
  const minuteStr = ((formData.get('minute') as string) || '').trim();
  const tempStr = ((formData.get('temperature') as string) || '').trim();
  const humidityStr = ((formData.get('humidity') as string) || '').trim();
  const baroStr = ((formData.get('baro') as string) || '').trim();

  // Validation (baro is optional)
  if (hourStr === '' || minuteStr === '' || tempStr === '' || humidityStr === '') {
    toast.error('Time, temperature and humidity are required');
    return;
  }

  const hourVal = Number(hourStr);
  const minuteVal = Number(minuteStr);
  const tempVal = Number(tempStr);
  const humidityVal = Number(humidityStr);

  if (Number.isNaN(hourVal) || hourVal < 0 || hourVal > 23) {
    toast.error('Hour must be between 0 and 23');
    return;
  }
  if (Number.isNaN(minuteVal) || minuteVal < 0 || minuteVal > 59) {
    toast.error('Minute must be between 0 and 59');
    return;
  }
  if (Number.isNaN(tempVal)) {
    toast.error('Temperature must be a valid number');
    return;
  }
  if (Number.isNaN(humidityVal) || humidityVal < 0 || humidityVal > 100) {
    toast.error('Humidity must be a percentage between 0 and 100');
    return;
  }
  let baroPa: number | null = null;
  if (baroStr !== '') {
    const baroHpa = Number(baroStr);
    if (Number.isNaN(baroHpa) || baroHpa < 500 || baroHpa > 1100) {
      toast.error('Pressure must be between 500 and 1100 hPa (leave blank for none)');
      return;
    }
    baroPa = baroHpa * 100;
  }

  onSave({ hour: hourVal, minute: minuteVal, temperature: tempVal, humidityPct: humidityVal, baroPa });
};

const ManualWeatherRowForm = ({
  defaultHour,
  defaultMinute,
  defaultTemp,
  defaultHumidity,
  defaultBaroHpa,
  isUpdating,
  onSave,
  onCancel,
}: {
  defaultHour: number | string;
  defaultMinute: number | string;
  defaultTemp: number | string;
  defaultHumidity: number | string;
  defaultBaroHpa: number | string;
  isUpdating: boolean;
  onSave: (values: ManualRowValues) => void;
  onCancel: () => void;
}): ReactElement => {
  const inputClass =
    'cr-w-20 cr-rounded cr-border cr-border-gray-300 cr-px-2 cr-py-1 cr-text-sm '
    + 'focus:cr-border-blue-500 focus:cr-outline-none';
  return (
    <form
      onSubmit={(e) => validateAndSubmitWeatherRow(e, onSave)}
      className="cr-flex cr-flex-wrap cr-items-end cr-gap-2"
    >
      <div className="cr-flex cr-flex-col cr-gap-1">
        <label className="cr-text-xs cr-font-medium cr-text-gray-700">Hour (0-23):</label>
        <input name="hour" type="text" inputMode="numeric" defaultValue={defaultHour} className={inputClass} />
      </div>
      <div className="cr-flex cr-flex-col cr-gap-1">
        <label className="cr-text-xs cr-font-medium cr-text-gray-700">Minute (0-59):</label>
        <input name="minute" type="text" inputMode="numeric" defaultValue={defaultMinute} className={inputClass} />
      </div>
      <div className="cr-flex cr-flex-col cr-gap-1">
        <label className="cr-text-xs cr-font-medium cr-text-gray-700">Temp (°C):</label>
        <input name="temperature" type="text" inputMode="decimal" defaultValue={defaultTemp} className={inputClass} />
      </div>
      <div className="cr-flex cr-flex-col cr-gap-1">
        <label className="cr-text-xs cr-font-medium cr-text-gray-700">Humidity (%):</label>
        <input name="humidity" type="text" inputMode="decimal" defaultValue={defaultHumidity} className={inputClass} />
      </div>
      <div className="cr-flex cr-flex-col cr-gap-1">
        <label className="cr-text-xs cr-font-medium cr-text-gray-700">Baro (hPa):</label>
        <input
          name="baro"
          type="text"
          inputMode="decimal"
          defaultValue={defaultBaroHpa}
          placeholder="optional"
          className={inputClass}
        />
      </div>
      <div className="cr-flex cr-items-center cr-gap-2">
        <button
          type="submit"
          disabled={isUpdating}
          className="cr-rounded cr-bg-blue-600 cr-px-3 cr-py-1 cr-text-sm cr-text-white hover:cr-bg-blue-700 disabled:cr-opacity-50"
          title="Save"
        >
          <HiOutlineCheckCircle className="cr-h-5 cr-w-5" />
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isUpdating}
          className="cr-rounded cr-bg-gray-600 cr-px-3 cr-py-1 cr-text-sm cr-text-white hover:cr-bg-gray-700 disabled:cr-opacity-50"
          title="Cancel"
        >
          <HiOutlineXMark className="cr-h-5 cr-w-5" />
        </button>
      </div>
    </form>
  );
};

interface ManualWeatherTableProps {
  rows: any[];
  utcOffsetSeconds: number;
  editingWeatherRow: number | null;
  isUpdating: boolean;
  onSave: (values: ManualRowValues) => void;
  onEditRow: (row: any) => void;
  onRemoveRow: (timeAbs: number) => void;
  onAddRow: () => void;
  onCancelEdit: () => void;
}

/** Manual weather entry card body — humidity is percent (0-100), pressure in hPa (stored Pa). */
export default function ManualWeatherTable({
  rows,
  utcOffsetSeconds,
  editingWeatherRow,
  isUpdating,
  onSave,
  onEditRow,
  onRemoveRow,
  onAddRow,
  onCancelEdit,
}: ManualWeatherTableProps): ReactElement {
  return (
    <div className="cr-mt-4 cr-rounded-lg cr-bg-gray-50 cr-p-4 cr-shadow-md">
      <div className="cr-mb-3 cr-flex cr-items-center cr-gap-2">
        <h5 className="cr-font-semibold cr-text-gray-800">Manual Weather Entry</h5>
        {isUpdating && <ImSpinner2 className="cr-h-5 cr-w-5 cr-animate-spin cr-text-blue-600" />}
      </div>
      <p className="cr-mb-4 cr-text-sm cr-text-gray-600">
        Manually enter weather data for a specific time. Humidity is percent; pressure is optional.
      </p>
      <div className="cr-mb-4">
        {rows.length > 0 || editingWeatherRow === -1 ? (
          <div className="cr-overflow-x-auto">
            <table className="cr-min-w-full cr-text-sm">
              <thead>
                <tr className="cr-text-left cr-text-xs cr-font-semibold cr-uppercase cr-text-gray-500">
                  <th className="cr-py-2 cr-pr-4">Time</th>
                  <th className="cr-py-2 cr-pr-4">Temperature</th>
                  <th className="cr-py-2 cr-pr-4">Humidity</th>
                  <th className="cr-py-2 cr-pr-4">Pressure</th>
                  <th className="cr-py-2">Action</th>
                </tr>
              </thead>
              <tbody className="cr-divide-y cr-divide-gray-200">
                {rows.map((row: any, index: number) => {
                  const asNumber = (v: any): number | null =>
                    typeof v === 'number' ? v : v != null && !Number.isNaN(Number(v)) ? Number(v) : null;
                  const tempValue = asNumber(row?.Temp_T);
                  const humidityValue = asNumber(row?.Humidity_x);
                  const baroValue = asNumber(row?.Baro_P);
                  const timeValue = Number(row?.TimeAbs_t);
                  const hasValidTime = !Number.isNaN(timeValue);
                  // Render TimeAbs_t (a UTC epoch) at the recording-location wall
                  // clock, matching the weather plots (shift by utc_offset, then read
                  // UTC parts via toISOString) so the viewer's browser zone stays out.
                  const recordingDate = hasValidTime
                    ? new Date((timeValue + utcOffsetSeconds) * 1000)
                    : null;
                  const dateLabel = recordingDate
                    ? recordingDate.toISOString().slice(0, 16).replace('T', ' ')
                    : '—';
                  const tempLabel = tempValue != null ? `${tempValue.toFixed(1)} °C` : '—';
                  const humidityLabel = humidityValue != null ? `${humidityValue.toFixed(0)} %` : '—';
                  const baroLabel = baroValue != null ? `${(baroValue / 100).toFixed(1)} hPa` : '—';
                  const key = `${row?.TimeAbs_t ?? 'row'}-${index}`;
                  const isEditing = editingWeatherRow === row?.TimeAbs_t;

                  if (isEditing) {
                    // Editing mode - show inline form
                    const hour = recordingDate ? recordingDate.getUTCHours() : 12;
                    const minute = recordingDate ? recordingDate.getUTCMinutes() : 0;
                    return (
                      <tr key={key} className="cr-bg-blue-50">
                        <td className="cr-py-2 cr-pr-4" colSpan={5}>
                          <ManualWeatherRowForm
                            defaultHour={hour}
                            defaultMinute={minute}
                            defaultTemp={tempValue ?? 20}
                            defaultHumidity={humidityValue ?? 50}
                            defaultBaroHpa={baroValue != null ? (baroValue / 100).toFixed(1) : ''}
                            isUpdating={isUpdating}
                            onSave={onSave}
                            onCancel={onCancelEdit}
                          />
                        </td>
                      </tr>
                    );
                  }

                  // View mode - show data
                  return (
                    <tr key={key} className="cr-text-gray-700">
                      <td className="cr-py-2 cr-pr-4">{dateLabel}</td>
                      <td className="cr-py-2 cr-pr-4">{tempLabel}</td>
                      <td className="cr-py-2 cr-pr-4">{humidityLabel}</td>
                      <td className="cr-py-2 cr-pr-4">{baroLabel}</td>
                      <td className="cr-py-2">
                        <div className="cr-flex cr-items-center cr-gap-2">
                          <button
                            type="button"
                            className="cr-text-gray-600 hover:cr-text-gray-900 disabled:cr-cursor-not-allowed disabled:cr-opacity-60"
                            onClick={() => onEditRow(row)}
                            disabled={isUpdating || editingWeatherRow !== null}
                            title="Edit"
                          >
                            <HiOutlinePencil className="cr-h-4 cr-w-4" />
                          </button>
                          <button
                            type="button"
                            className="cr-text-red-600 hover:cr-text-red-800 disabled:cr-cursor-not-allowed disabled:cr-opacity-60"
                            onClick={() => onRemoveRow(row?.TimeAbs_t)}
                            disabled={isUpdating || editingWeatherRow !== null}
                            title="Remove"
                          >
                            <HiOutlineTrash className="cr-h-4 cr-w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {/* New row form when adding */}
                {editingWeatherRow === -1 && (
                  <tr className="cr-bg-blue-50">
                    <td className="cr-py-2 cr-pr-4" colSpan={5}>
                      <ManualWeatherRowForm
                        defaultHour="12"
                        defaultMinute="0"
                        defaultTemp="20"
                        defaultHumidity="50"
                        defaultBaroHpa=""
                        isUpdating={isUpdating}
                        onSave={onSave}
                        onCancel={onCancelEdit}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="cr-text-sm cr-text-gray-500">No manual weather entries yet.</p>
        )}
      </div>
      {/* Add button - only show when not editing */}
      {editingWeatherRow === null && (
        <div className="cr-flex cr-justify-end">
          <button
            type="button"
            onClick={onAddRow}
            disabled={isUpdating}
            className="cr-flex cr-items-center cr-gap-1 cr-rounded-md cr-p-2 cr-text-sm cr-text-gray-600 hover:cr-bg-gray-100 hover:cr-text-gray-900 disabled:cr-opacity-50"
            title="Add Manual Entry"
          >
            <HiPlus className="cr-h-4 cr-w-4" />
            <span>ENTRY</span>
          </button>
        </div>
      )}
    </div>
  );
}
